// @ts-nocheck
import { useCallback } from "react";
import PropTypes from "prop-types";
import {
  AsyncPaginate,
  reduceGroupedOptions,
} from "react-select-async-paginate";
import { useTranslation, i18n } from "react-i18next";
import translations from '../../data/ingredients_translations.json'
import FrTranslation from '../../data/eng-to-fr-ingredients.json'

import request from "services/request";
import { useUserData } from "contexts/AuthContextManagement";
import { ITEMS_PER_PAGE } from "common/constants";
import { components } from "react-select";
import { getResponse, searchIngredients } from "common/utils";
import { IoInformationCircleOutline  } from 'react-icons/io5'; // Importing an info icon from react-icons

type SelectAsyncPaginateProps = {
  datasource?: Array<any>;
  query: string;
  mapper?: Function;
  onChange: Function;
  dataField: string;
  value: string;
  placeholder: string;
  isMulti?: boolean;
  multiOptions?: Array<any>;
  count?: number;
  isMyMeals?: boolean;
  isDisabled?: boolean;
  isEvent?: boolean
  is_demo?: boolean
};

const CustomInput = (inputProps: any) => {
  const count = (inputProps.selectProps?.value as any[] ?? []).length

  if (count <= 2) {
    //@ts-ignore
    return <components.Input {...inputProps}></components.Input>;
  }

  return (
    //@ts-ignore
    <span style={{ display: "flex", alignItems: "center", color: "black" }}>
      <span>{`${count} items selected`}</span>
      <components.Input {...inputProps}></components.Input>
    </span>
  );
};



const SelectAsyncPaginate = (props: SelectAsyncPaginateProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language
  const { selectedRestaurantId, hasRetaurants, isRestaurantLoaded } =
    useUserData();

    const CustomSingleValue = ({ children, ...props }) => {
      return (
        <components.SingleValue {...props}>
          {children}  
          {props.data?.source === 'CHATGPT' && (
              <IoInformationCircleOutline title={t("The value of this ingredient has not been certified yet by an external organisation")} style={{ marginLeft: 5, zIndex: 999, fontSize: "17px", cursor: 'pointer' }} />
          )}
        </components.SingleValue>
      );
    };

    const colourStyles = {
      option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        return {
          ...styles,
          backgroundColor: isDisabled
            ? undefined
            : isSelected
              ? data.color
              : isFocused
                ? "#F3F4FB"
                : undefined,
          // Check for the special source and set the text color accordingly
          color: isDisabled 
                  ? "#ccc" 
                  : data.source === 'get-co2-emission' 
                    ? '#6353ea' 
                    : 'black',
        };
      },
      menuPortal: (provided) => ({ ...provided, zIndex: 99999 }),
      menu: (provided) => ({ ...provided, zIndex: 99999 }),
      multiValueLabel: (base) => ({ ...base }),
      singleValue: (styles, { data }) => ({
        ...styles,
        // Use the special color if the option comes from "get-co2-emission"
        color: data?.source === 'get-co2-emission' || data?.source === 'CHATGPT' ? '#6353ea' : 'black',
        display: 'flex',
        alignItems: 'center',
      }),
    };

  const translateIngredient = (name) => {
    if (!name || currentLanguage !== 'fr') return name;
    // Reverse lookup in translations (French→English): find the French key whose English value matches
    const exactReverse = Object.entries(translations).find(([, enName]) => enName === name);
    if (exactReverse) return exactReverse[0];
    const lowerName = name.toLowerCase();
    const looseReverse = Object.entries(translations).find(([, enName]) => enName?.toLowerCase() === lowerName);
    if (looseReverse) return looseReverse[0];
    // Fallback to eng-to-fr translation
    const direct = FrTranslation[name];
    if (direct) return Array.isArray(direct) ? direct[0] : direct;
    const match = Object.entries(FrTranslation).find(([key]) => key.toLowerCase() === lowerName);
    if (match) return Array.isArray(match[1]) ? match[1][0] : match[1];
    return name;
  };

  const loadOptions = async (
    searchQuery,
    loadedOptions,
    { page }
  ): Promise<any> => {
    if (props?.is_demo) {
      return {
        options: props.datasource?.map(i => {
          return {
            ...i,
            id: i.item_id
          }
        }),
        hasMore: false,
        additional: { page: 1 },
      };
    }

    if (!selectedRestaurantId && props.is_demo) {
      return { options: [], hasMore: false, additional: { page: 1 } };
    }
    let updatedSearchQuery = searchQuery?.trim().replace(/\s+/g, ' ')
    let matchingElement = []
    if (currentLanguage == 'fr' && props.query === 'carbon-footprints/ingredients' && updatedSearchQuery) {
      matchingElement = Object.keys(translations).filter(key => key?.toLowerCase().includes(updatedSearchQuery?.toLowerCase()))
      matchingElement = searchIngredients(updatedSearchQuery, matchingElement)
      const seen = new Set();
      matchingElement = matchingElement?.sort((a, b) => a.length - b.length).filter(item => {
        const lower = item?.toLowerCase();
        if (seen.has(lower)) return false;
        seen.add(lower);
        return true;
      });
    }else if(currentLanguage == 'fr' && props.query === 'carbon-footprints/ingredients') {
      matchingElement = Object.keys(translations).slice(0, 100)
      const seen = new Set();
      matchingElement = matchingElement?.sort((a, b) => a.length - b.length).filter(item => {
        const lower = item?.toLowerCase();
        if (seen.has(lower)) return false;
        seen.add(lower);
        return true;
      });
    }
    const payload = {
      ...(props.query !== 'brands' && { restaurant_id: selectedRestaurantId }),
      limit: props.query === 'carbon-footprints/ingredients' ? 100 : ITEMS_PER_PAGE,
      page,
      ...(props.dataField === "is_external" && { is_external: true }),
      ...(searchQuery && { search: updatedSearchQuery }),
    };
    if (props.query === 'carbon-footprints/ingredients') {
      delete payload.restaurant_id
    }

    if(props.dataField === 'cooking_state') {
      delete payload.restaurant_id
      delete payload.limit
      delete payload.page
    }
    let response
    response = await request.get(props.query, payload, true, false);
    if (matchingElement.length) {
      const existingNames = new Set(
        response.cf_ingredients.flatMap(i => [
          i.name?.toLowerCase(),
          translateIngredient(i?.name)?.toLowerCase()
        ])
      );
      const additionalTranslations = matchingElement
        .filter(item => !existingNames.has(item?.toLowerCase()))
        .map(item => ({ name: item, french: currentLanguage == 'fr' }));
      response.cf_ingredients = [...response.cf_ingredients, ...additionalTranslations];
    }
    if(props.query === 'carbon-footprints/ingredients') {
      if(response.cf_ingredients.length > 0 && updatedSearchQuery) {
        if((response.cf_ingredients.findIndex((item) => item.name.toLowerCase() === updatedSearchQuery.toLowerCase())) === -1) {
          let asd = await request.get("carbon-footprints/ingredients/get-co2-emission", { qry: updatedSearchQuery }, true, false)
          response.cf_ingredients.unshift({
            id: null,
            name: asd.co2_emission.qry,
            carbon_emission: asd.co2_emission.co2_impact_per_kg,
            unit: "kg",
            french: currentLanguage == 'fr',
            format: 1,
            source: 'get-co2-emission', // Mark this option so you know it came from this API
            ingredient_id: {
              id: null,
              name: asd.co2_emission.qry,
              unit: "kg",
            }
          })
        }else {
          response = {...response, cf_ingredients: response.cf_ingredients.filter(item => item.source !== 'CHATGPT')}
        }
      }else if (response?.cf_ingredients?.length === 0) {
        response = await request.get("carbon-footprints/ingredients/get-co2-emission", { qry: searchQuery }, true, false);
        response = {
          cf_ingredients: [
            {
              id: null,
              name: response.co2_emission.qry,
              carbon_emission: response.co2_emission.co2_impact_per_kg,
              unit: "kg",
              french: currentLanguage == 'fr',
              format: 1,
              source: 'get-co2-emission',
              ingredient_id: {
                id: null,
                name: response.co2_emission.qry,
                unit: "kg",
              }
            }
          ]
        }
      }
    }

    let optionsObj = {
      options: props.mapper
        ? props.mapper(props.query === 'carbon-footprints/ingredients' ? response['cf_ingredients'].filter(item => item.source !== 'CHATGPT').map(i => ({ ...i, name: translateIngredient(i?.name), ingredient_id: { id: i?.id, name: translateIngredient(i?.name), unit: i?.unit } }))?.sort((a, b) => a.name.length - b.name.length) : response[props.query])
        : getResponse(response, props.query),
      ...(props.multiOptions && {
        options: [{ label: "Meals", options: response[props.query] }],
      }),
      ...(payload.page === 1 &&
        props.multiOptions && {
        options: [
          { label: "Meal Lists", options: props.multiOptions },
          { label: t("Meals"), options: response[props.query] },
        ],
      }),
      hasMore: response.total_pages > response.page,
      additional: { page: response.page + 1 },
    };
    if (props.isMyMeals) {
      return {
        ...optionsObj, options: optionsObj.options.map(i => {
          return {
            ...i,
            name: i.is_external ? i.name : i.name + " - " + t("Recipe")
          }
        })
      }
    }
    return optionsObj
  };

  const onChange = (option) => {
    if (typeof props.onChange === "function") {
      props.onChange(props.dataField)(option);
    }
  };

  const additionalProps = props.isMulti 
  ? {
      styles: {
        ...colourStyles,
        multiValue: (base) => ({
          ...base,
          display: props.count && props.count > 2 ? 'none' : 'flex',
        })
      },
      components: {
        Input: CustomInput,
        SingleValue: CustomSingleValue,
      },
    } 
  : {
      styles: { ...colourStyles },
      components: {
        SingleValue: CustomSingleValue,
      },
    };

  let name = currentLanguage == 'fr' && props.query === 'carbon-footprints/ingredients' ? translateIngredient(props.value?.ingredient_id?.name) : props.value?.ingredient_id?.name

  return (
    <AsyncPaginate
      menuPortalTarget={document.body}
      menuPosition={"fixed"}
      value={props.value?.ingredient_id ? { ...props.value, id: props.value?.ingredient_id?.id, name: name  } : { ...props.value }}
      key={props.value}
      //@ts-ignore
      loadOptions={loadOptions}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.name}
      onChange={onChange}
      isSearchable={true}
      {...additionalProps}
      isDisabled={props.isDisabled}
      placeholder={props.placeholder}
      additional={{ page: 1 }}
      debounceTimeout={1000}
      closeMenuOnSelect={props.isMulti ? false : true}
      hideSelectedOptions={false}
      isMulti={props.isMulti || false}
      {...(props.multiOptions && { reduceOptions: reduceGroupedOptions })}
    />
  );
};

SelectAsyncPaginate.propTypes = {
  dataField: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  query: PropTypes.string,
  mapper: PropTypes.func,
  key: PropTypes.string,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
};

export default SelectAsyncPaginate;
